/**
 * Create record — /m/$moduleKey/$entityKey/new
 *
 * Loads the entity definition and renders an <EntityForm> with empty
 * defaults. On submit, calls records.create and navigates to the
 * new record's detail page on success.
 */

import type { FieldDefinition } from '@cloud/custom-modules'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { ArrowLeft } from 'lucide-react'
import { toast } from 'sonner'
import { EntityForm } from '@/components/custom/entity-form'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { orpc } from '@/lib/orpcClient'

export const Route = createFileRoute('/m/$moduleKey/$entityKey/new')({
  component: NewRecordPage,
})

function NewRecordPage() {
  const { moduleKey, entityKey } = Route.useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const { data: entity, isLoading } = useQuery(
    orpc.modules.custom.entities.get.queryOptions({
      input: { moduleKey, key: entityKey },
    }),
  )

  const createMutation = useMutation(
    orpc.modules.custom.records.create.mutationOptions({
      onSuccess: (record) => {
        toast.success(`${entity?.name ?? 'Record'} created`)
        // Invalidate list queries so the new record appears
        void queryClient.invalidateQueries({
          queryKey: orpc.modules.custom.records.list.queryKey({
            input: { moduleKey, entityKey },
          }),
        })
        void navigate({
          to: '/m/$moduleKey/$entityKey/$recordId',
          params: { moduleKey, entityKey, recordId: record.id },
        })
      },
      onError: (err) => toast.error(err.message || 'Failed to create record'),
    }),
  )

  if (isLoading) {
    return (
      <div className="container mx-auto py-6 px-4 space-y-4 max-w-2xl">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-96 w-full" />
      </div>
    )
  }

  if (!entity) {
    return (
      <div className="container mx-auto py-6 px-4">
        <h1 className="text-2xl font-bold">Entity not found</h1>
      </div>
    )
  }

  return (
    <div className="container mx-auto py-6 px-4 max-w-2xl space-y-4">
      <Link
        to="/m/$moduleKey/$entityKey"
        params={{ moduleKey, entityKey }}
        className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4 mr-1" />
        Back to {entity.namePlural ?? `${entity.name}s`}
      </Link>

      <Card>
        <CardHeader>
          <CardTitle>New {entity.name}</CardTitle>
        </CardHeader>
        <CardContent>
          <EntityForm
            fields={entity.fields as FieldDefinition[]}
            submitting={createMutation.isPending}
            submitLabel={`Create ${entity.name}`}
            onCancel={() =>
              navigate({
                to: '/m/$moduleKey/$entityKey',
                params: { moduleKey, entityKey },
              })
            }
            onSubmit={async (data) => {
              await createMutation.mutateAsync({ moduleKey, entityKey, data })
            }}
          />
        </CardContent>
      </Card>
    </div>
  )
}
